/* ==========================================================================
   SOUNDLABZ — audio.js
   The site is about sound. This is where it stops being silent.

   Everything is user-initiated. No AudioContext exists until someone presses
   a button, so there is nothing to autoplay-block and nothing running idle.

   Exposes window.SLZAudio, which theme.js's Wave reads to drive amplitude —
   so the waveform stops tracking scroll and starts tracking actual signal.
   ========================================================================== */
(function () {
  'use strict';

  var $ = function (s, r) { return (r || document).querySelector(s); };
  var $$ = function (s, r) { return Array.prototype.slice.call((r || document).querySelectorAll(s)); };

  var ctx = null, master = null, limiter = null, analyser = null, data = null;
  var voice = null, voiceGain = null;
  var state = {
    active: false,
    freq: 0,
    target: 0,
    sweeping: false,
    raf: 0,
    sweepEnd: 0,
    sweepFrom: 0,
    sweepTo: 0,
    sweepDur: 0,
    sweepStart: 0,
    volume: 0.55
  };

  /* --------------------------------------------------------- the graph */
  function boot() {
    if (ctx) return ctx;
    var AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();

    // A compressor sitting on the master bus. Sine waves at full tilt are
    // brutal on small drivers and on ears — this is a seatbelt, not a sound.
    limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.knee.value = 0;
    limiter.ratio.value = 20;
    limiter.attack.value = 0.002;
    limiter.release.value = 0.15;

    master = ctx.createGain();
    master.gain.value = state.volume;

    analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    analyser.smoothingTimeConstant = 0.72;
    data = new Uint8Array(analyser.fftSize);

    master.connect(limiter);
    limiter.connect(analyser);
    analyser.connect(ctx.destination);
    return ctx;
  }

  function resume() {
    if (ctx && ctx.state === 'suspended') ctx.resume();
  }

  /* --------------------------------------------------------- one voice */
  function startVoice(freq) {
    boot();
    if (!ctx) return;
    resume();
    stopVoice(0.02);

    voice = ctx.createOscillator();
    voice.type = 'sine';
    voice.frequency.setValueAtTime(freq, ctx.currentTime);

    voiceGain = ctx.createGain();
    // Soft attack. A hard-edged start on a 30Hz sine is a speaker-killing thump.
    voiceGain.gain.setValueAtTime(0.0001, ctx.currentTime);
    voiceGain.gain.exponentialRampToValueAtTime(0.9, ctx.currentTime + 0.09);

    voice.connect(voiceGain);
    voiceGain.connect(master);
    voice.start();

    state.active = true;
    state.freq = freq;
    state.target = freq;
    paint();
  }

  function stopVoice(fade) {
    if (!voice) return;
    var f = fade == null ? 0.12 : fade;
    var v = voice, g = voiceGain;
    try {
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + f);
      v.stop(ctx.currentTime + f + 0.02);
    } catch (e) { /* already stopped */ }
    voice = null; voiceGain = null;
  }

  function stopAll() {
    state.sweeping = false;
    state.active = false;
    state.freq = 0;
    cancelAnimationFrame(state.raf);
    stopVoice(0.14);
    paint();
    document.body.removeAttribute('data-audio-on');
  }

  /* ------------------------------------------------------------- sweep */
  function sweep(from, to, seconds) {
    boot();
    if (!ctx) return;
    resume();
    startVoice(from);

    state.sweeping = true;
    state.sweepFrom = from;
    state.sweepTo = to;
    state.sweepDur = seconds * 1000;
    state.sweepStart = performance.now();

    // Exponential, because pitch is logarithmic — a linear 20→120Hz ramp
    // spends most of its time up top where nobody's listening.
    try {
      voice.frequency.cancelScheduledValues(ctx.currentTime);
      voice.frequency.setValueAtTime(from, ctx.currentTime);
      voice.frequency.exponentialRampToValueAtTime(to, ctx.currentTime + seconds);
    } catch (e) {}

    cancelAnimationFrame(state.raf);
    tickSweep();
  }

  function tickSweep() {
    if (!state.sweeping) return;
    var p = (performance.now() - state.sweepStart) / state.sweepDur;
    if (p >= 1) {
      state.sweeping = false;
      stopAll();
      return;
    }
    state.freq = state.sweepFrom * Math.pow(state.sweepTo / state.sweepFrom, p);
    paint(p);
    state.raf = requestAnimationFrame(tickSweep);
  }

  /* ------------------------------------------------------- the readout */
  function paint(progress) {
    var hz = Math.round(state.freq);
    $$('[data-audio-hz]').forEach(function (el) {
      el.textContent = state.active ? hz : '—';
    });
    $$('[data-audio-band]').forEach(function (el) {
      el.textContent = state.active ? band(hz) : 'Idle';
    });
    $$('[data-audio-progress]').forEach(function (el) {
      el.style.setProperty('--p', ((progress || 0) * 100).toFixed(1) + '%');
    });
    $$('[data-audio-toggle]').forEach(function (el) {
      el.setAttribute('aria-pressed', state.active ? 'true' : 'false');
    });
    if (state.active) document.body.setAttribute('data-audio-on', '');
  }

  // What you're actually listening to, in plain language.
  function band(hz) {
    if (hz < 25) return 'Infrasonic — you feel this, you don\'t hear it';
    if (hz < 35) return 'Subsonic — where SPL builds live';
    if (hz < 50) return 'Deep bass — daily driver territory';
    if (hz < 70) return 'Kick drum, 808s';
    if (hz < 100) return 'Upper bass — most subs are loudest here';
    if (hz < 160) return 'Midbass — door speaker range';
    return 'Low mids';
  }

  /* ----------------------------------------------------------- the API */
  window.SLZAudio = {
    tone: function (hz) {
      state.sweeping = false;
      cancelAnimationFrame(state.raf);
      startVoice(hz);
    },
    sweep: sweep,
    stop: stopAll,
    isActive: function () { return state.active; },
    freq: function () { return state.freq; },
    setVolume: function (v) {
      state.volume = Math.max(0, Math.min(1, v));
      if (master) master.gain.setTargetAtTime(state.volume, ctx.currentTime, 0.05);
    },
    // 0..1 RMS of what's actually leaving the bus. This is what the Wave reads.
    level: function () {
      if (!analyser || !state.active) return 0;
      analyser.getByteTimeDomainData(data);
      var sum = 0;
      for (var i = 0; i < data.length; i += 4) {
        var d = (data[i] - 128) / 128;
        sum += d * d;
      }
      var rms = Math.sqrt(sum / (data.length / 4));
      return Math.min(rms * 2.4, 1);
    }
  };

  /* -------------------------------------------------------- the wiring */
  function wire() {
    // Single tones: <button data-tone="40">
    $$('[data-tone]').forEach(function (btn) {
      if (btn.__slz) return; btn.__slz = 1;
      btn.addEventListener('click', function () {
        var hz = parseFloat(btn.getAttribute('data-tone'));
        if (state.active && Math.round(state.freq) === Math.round(hz) && !state.sweeping) {
          stopAll();
        } else {
          window.SLZAudio.tone(hz);
          $$('[data-tone]').forEach(function (b) { b.classList.toggle('is-on', b === btn); });
        }
      });
    });

    // Sweeps: <button data-sweep data-from="20" data-to="120" data-dur="14">
    $$('[data-sweep]').forEach(function (btn) {
      if (btn.__slz) return; btn.__slz = 1;
      btn.addEventListener('click', function () {
        if (state.sweeping) { stopAll(); return; }
        sweep(
          parseFloat(btn.getAttribute('data-from') || 20),
          parseFloat(btn.getAttribute('data-to') || 120),
          parseFloat(btn.getAttribute('data-dur') || 14)
        );
      });
    });

    $$('[data-audio-stop]').forEach(function (btn) {
      if (btn.__slz) return; btn.__slz = 1;
      btn.addEventListener('click', stopAll);
    });

    $$('[data-audio-volume]').forEach(function (sl) {
      if (sl.__slz) return; sl.__slz = 1;
      sl.addEventListener('input', function () {
        window.SLZAudio.setVolume(parseFloat(sl.value) / 100);
      });
    });

    // Manual frequency scrub
    $$('[data-audio-scrub]').forEach(function (sl) {
      if (sl.__slz) return; sl.__slz = 1;
      sl.addEventListener('input', function () {
        var hz = parseFloat(sl.value);
        $$('[data-audio-hz]').forEach(function (el) { el.textContent = Math.round(hz); });
        $$('[data-audio-band]').forEach(function (el) { el.textContent = band(hz); });
        if (state.active && !state.sweeping && voice) {
          voice.frequency.setTargetAtTime(hz, ctx.currentTime, 0.02);
          state.freq = hz;
        }
      });
      sl.addEventListener('change', function () {
        if (!state.active) window.SLZAudio.tone(parseFloat(sl.value));
      });
    });
  }

  // Anything that takes you away from the page should shut the tone off.
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopAll();
  });
  window.addEventListener('pagehide', stopAll);

  if (document.readyState !== 'loading') wire();
  else document.addEventListener('DOMContentLoaded', wire);
  document.addEventListener('shopify:section:load', wire);
})();
